import { useParams, Navigate, Link } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import MarketingLayout from '../layouts/MarketingLayout.jsx'
import SEOHead from '../components/SEOHead.jsx'
import CTAButton from '../components/CTAButton.jsx'
import { BLOG_POSTS } from '../data/blog.js'

const BLOG_MODULES = {
  'alternative-kahoot-gratuite':       lazy(() => import('../content/blog/alternative-kahoot-gratuite.mdx')),
  'reviser-brevet-quiz-interactif':    lazy(() => import('../content/blog/reviser-brevet-quiz-interactif.mdx')),
}

function PostSkeleton() {
  return (
    <div className="animate-pulse space-y-4 max-w-2xl mx-auto py-16">
      <div className="h-6 bg-slate-200 rounded w-3/4" />
      <div className="h-4 bg-slate-100 rounded w-full" />
      <div className="h-4 bg-slate-100 rounded w-5/6" />
      <div className="h-4 bg-slate-100 rounded w-full" />
    </div>
  )
}

export default function BlogPostPage() {
  const { slug } = useParams()
  const meta = BLOG_POSTS.find(p => p.slug === slug)
  const PostContent = BLOG_MODULES[slug]

  if (!meta || !PostContent) return <Navigate to="/blog" replace />

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: meta.title,
    description: meta.description,
    datePublished: meta.date,
    dateModified: meta.date,
    author: {
      '@type': 'Organization',
      name: 'KartQuiz',
      url: 'https://kartquiz.fr',
    },
    publisher: {
      '@type': 'Organization',
      name: 'KartQuiz',
      url: 'https://kartquiz.fr',
    },
    url: `https://kartquiz.fr/blog/${slug}`,
    keywords: meta.tags.join(', '),
  }

  return (
    <MarketingLayout>
      <SEOHead
        title={`${meta.title} | KartQuiz`}
        description={meta.description}
        canonical={`https://kartquiz.fr/blog/${slug}`}
        jsonLd={[jsonLd]}
      />

      {/* Article header */}
      <header className="bg-[#F8FAFC] py-16 md:py-24 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 space-y-5">
          <Link to="/blog" className="text-sm text-slate-500 hover:text-kart-blue transition-colors inline-flex items-center gap-1">
            ← Blog
          </Link>
          <div className="flex flex-wrap gap-2">
            {meta.tags.map(tag => (
              <span key={tag} className="text-xs font-semibold uppercase tracking-wide bg-kart-blue/10 text-kart-blue px-2.5 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>
          <h1 className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl text-kart-night leading-tight">
            {meta.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <time dateTime={meta.date}>
              {new Date(meta.date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </time>
            <span>·</span>
            <span>{meta.readTime} de lecture</span>
            <span>·</span>
            <span className="text-slate-400">Mis à jour en mai 2026</span>
          </div>
        </div>
      </header>

      {/* Article body + TOC */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* TOC sidebar */}
            {meta.toc && meta.toc.length > 0 && (
              <aside className="lg:w-60 shrink-0">
                <div className="sticky top-24">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">Sommaire</p>
                  <nav className="space-y-1">
                    {meta.toc.map(item => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className="block text-sm text-slate-500 hover:text-kart-blue transition-colors py-1 leading-snug"
                      >
                        {item.label}
                      </a>
                    ))}
                  </nav>
                </div>
              </aside>
            )}

            {/* Content */}
            <article className="flex-1 min-w-0 prose prose-slate prose-lg max-w-2xl
              prose-headings:font-display prose-headings:font-semibold prose-headings:text-kart-night
              prose-a:text-kart-blue prose-a:no-underline hover:prose-a:underline
              prose-strong:text-kart-night
              prose-blockquote:border-kart-blue prose-blockquote:bg-kart-blue/5 prose-blockquote:rounded-r-xl prose-blockquote:py-1
              prose-table:text-sm prose-th:bg-slate-50
              prose-code:text-kart-action prose-code:bg-kart-action/5 prose-code:px-1 prose-code:rounded
            ">
              <Suspense fallback={<PostSkeleton />}>
                <PostContent />
              </Suspense>
            </article>
          </div>
        </div>
      </div>

      {/* CTA */}
      <section className="bg-kart-night py-20">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <h2 className="font-display font-semibold text-3xl text-white">
            Envie de tester KartQuiz en classe ?
          </h2>
          <p className="text-slate-300">
            Un compte gratuit, 30 secondes, et ta première partie peut démarrer.
          </p>
          <CTAButton to="/login" size="lg">Créer un compte gratuit</CTAButton>
        </div>
      </section>
    </MarketingLayout>
  )
}
